import type { State } from 'src/replicant/State';
import * as Gifts from '.';
import * as Inventory from '../inventory';
import { getJSTDateTime, getJSTDaysDiff } from 'src/replicant/utils/time';

/** Check if we can send reward to this user */
export function canSendGift(
  state: State,
  targetUserId: string,
  now: number,
): boolean {
  // Check last sent gift for given user
  const gift = state.gifts.sent[targetUserId];

  // If gift was never sent, we can send gift to this user
  if (!gift) return true;

  const sentAtJST = getJSTDateTime(gift.sentAt);
  const nextDayStartJST = sentAtJST.plus({ days: 1 }).startOf('day');
  const nowJST = getJSTDateTime(now);

  return nowJST >= nextDayStartJST;
}

/** Can collect reward from specific user */
export function canCollect(
  state: State,
  senderUserId: string,
  now: number,
): boolean {
  const gift = state.gifts.received[senderUserId];
  const giftEnergy = gift?.energy;
  const giftPoints = gift?.bPoints;
  const giftGems = gift?.gems;

  // No received gift - No reward
  if (!giftEnergy && !giftPoints && !giftGems) return false;

  // Check if we didn't overflow max energy
  if (giftEnergy) {
    const energyNow = Inventory.getters.getEnergy(state, now);
    const total = energyNow + giftEnergy;

    // If we have more energy then maxClaimEnergy do not allow claim new one
    if (total > Gifts.ruleset.maxClaimEnergy) return false;
  }

  // All check passed we can collect
  return true;
}

/** Check if gift exists; doesn't check if current user can collect it */
export function hasGift(state: State, senderUserId: string): boolean {
  const gift = state.gifts.received[senderUserId];
  if (!gift) return false;
  const { energy, bPoints, gems } = gift;
  return !!energy || !!bPoints || !!gems;
}

/** Get total rewards available for collect */
export function getAvailableGiftRewardsTotal(
  state: State,
  now: number,
): { energy: number; bPoints: number; gems: number } {
  const gifts = state.gifts.received;
  const max = Gifts.ruleset.maxClaimEnergy;
  const current = Inventory.getters.getEnergy(state, now);

  const rewards = { energy: 0, bPoints: 0, gems: 0 };
  Object.values(gifts).forEach(({ energy, bPoints, gems }) => {
    rewards.energy += energy ?? 0;
    rewards.bPoints += bPoints ?? 0;
    rewards.gems += gems ?? 0;
  });

  const diff = max - (current + rewards.energy);
  const available = rewards.energy + Math.min(0, diff);
  rewards.energy = Math.max(0, available);

  // Only whole gifts that fit into today's allowance can be collected
  const collectableGifts = Math.floor(
    getGiftGemsRemainingToday(state, now) / Gifts.ruleset.gemGiftAmount,
  );
  rewards.gems = Math.min(
    rewards.gems,
    collectableGifts * Gifts.ruleset.gemGiftAmount,
  );

  return rewards;
}

/** Gems collected from gifts today. Resets at 00:00 JST. */
export function getGiftGemsCollectedToday(state: State, now: number): number {
  const { gems, collectedAt } = state.gifts.collected;
  return getJSTDaysDiff(now, collectedAt) === 0 ? gems : 0;
}

/** Gems that can still be collected from gifts today */
export function getGiftGemsRemainingToday(state: State, now: number): number {
  return Math.max(
    0,
    Gifts.ruleset.maxDailyGiftGems - getGiftGemsCollectedToday(state, now),
  );
}

/**
 * Can one more gem gift be collected today.
 *
 * The daily cap is enforced on the client only (#12235), so actions must not
 * call this. See `consumeGift`.
 */
export function canCollectGiftGems(state: State, now: number): boolean {
  return getGiftGemsRemainingToday(state, now) >= Gifts.ruleset.gemGiftAmount;
}
